import { parseNetlifySiteRef, type NetlifySiteRef } from '../utils/netlifySiteRef';

const API_BASE = 'https://api.netlify.com/api/v1';

export type NetlifySiteSummary = {
  id: string;
  name: string;
  ssl_url: string;
  default_domain: string;
};

export type NetlifyDeploySummary = {
  id: string;
  state: string;
  branch: string | null;
  commit_ref: string | null;
  deploy_url: string;
  created_at: string | null;
  published_at: string | null;
  error_message: string | null;
};

export type NetlifyDeployCheckResult = {
  site: NetlifySiteSummary;
  deploy: NetlifyDeploySummary;
};

function authHeaders(apiToken: string): HeadersInit {
  return {
    Authorization: `Bearer ${apiToken.trim()}`,
    Accept: 'application/json',
  };
}

async function netlifyJson<T>(url: string, apiToken: string): Promise<T> {
  const res = await fetch(url, { headers: authHeaders(apiToken) });
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    const msg =
      typeof body === 'object' && body && 'message' in body
        ? String((body as { message: string }).message)
        : typeof body === 'string'
          ? body
          : res.statusText;
    throw new Error(`Netlify API ${res.status}: ${msg}`);
  }
  return body as T;
}

function siteMatchesRef(site: NetlifySiteSummary, ref: NetlifySiteRef): boolean {
  if (ref.kind === 'site_id') return site.id === ref.value;
  const slug = ref.value.toLowerCase();
  const name = site.name.toLowerCase();
  if (name === slug) return true;
  const host = (site.ssl_url || site.default_domain || '').toLowerCase();
  return host.includes(`${slug}.netlify.app`) || host.includes(slug);
}

/** List owned sites (first page; enough for personal accounts). */
export async function listNetlifySites(apiToken: string): Promise<NetlifySiteSummary[]> {
  const data = await netlifyJson<NetlifySiteSummary[]>(`${API_BASE}/sites?per_page=100`, apiToken);
  return Array.isArray(data) ? data : [];
}

/** Resolve site id from slug, deploy URL, admin URL, or raw API id. */
export async function resolveNetlifySiteId(
  apiToken: string,
  siteInput: string
): Promise<string> {
  const ref = parseNetlifySiteRef(siteInput);
  if (!ref) throw new Error('Invalid Netlify site URL or slug.');

  if (ref.kind === 'site_id') {
    await netlifyJson<NetlifySiteSummary>(`${API_BASE}/sites/${ref.value}`, apiToken);
    return ref.value;
  }

  const sites = await listNetlifySites(apiToken);
  const match = sites.find(s => siteMatchesRef(s, ref));
  if (!match) {
    throw new Error(`No Netlify site found for "${ref.value}". Check the project URL.`);
  }
  return match.id;
}

function mapDeploy(raw: Record<string, unknown>): NetlifyDeploySummary {
  return {
    id: String(raw.id ?? ''),
    state: String(raw.state ?? 'unknown'),
    branch: raw.branch != null ? String(raw.branch) : null,
    commit_ref: raw.commit_ref != null ? String(raw.commit_ref) : null,
    deploy_url: String(raw.deploy_ssl_url ?? raw.ssl_url ?? raw.deploy_url ?? ''),
    created_at: raw.created_at != null ? String(raw.created_at) : null,
    published_at: raw.published_at != null ? String(raw.published_at) : null,
    error_message: raw.error_message != null ? String(raw.error_message) : null,
  };
}

/** Latest deploy for a site (newest first). */
export async function fetchLatestNetlifyDeploy(
  apiToken: string,
  siteId: string
): Promise<NetlifyDeploySummary> {
  const data = await netlifyJson<Record<string, unknown>[]>(
    `${API_BASE}/sites/${siteId}/deploys?per_page=1`,
    apiToken
  );
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('No deploys found for this site.');
  }
  return mapDeploy(data[0]!);
}

/** Resolve site and return its latest deploy row. */
export async function checkNetlifyDeploy(
  apiToken: string,
  siteInput: string
): Promise<NetlifyDeployCheckResult> {
  const token = apiToken.trim();
  if (!token) throw new Error('Netlify API token is required.');
  const siteId = await resolveNetlifySiteId(token, siteInput);
  const site = await netlifyJson<NetlifySiteSummary>(`${API_BASE}/sites/${siteId}`, token);
  const deploy = await fetchLatestNetlifyDeploy(token, siteId);
  return { site, deploy };
}

export function formatNetlifyDeployStatus(result: NetlifyDeployCheckResult): string {
  const { site, deploy } = result;
  const branch = deploy.branch ? ` · ${deploy.branch}` : '';
  const commit = deploy.commit_ref ? ` · ${deploy.commit_ref.slice(0, 7)}` : '';
  const err = deploy.error_message ? ` — ${deploy.error_message}` : '';
  return `${site.name}: deploy ${deploy.state}${branch}${commit}${err}`;
}

export const TERMINAL_NETLIFY_DEPLOY_STATES = new Set(['ready', 'error', 'cancelled']);

export function isTerminalNetlifyDeployState(state: string): boolean {
  return TERMINAL_NETLIFY_DEPLOY_STATES.has(state.toLowerCase());
}
